import type {
  AcceptedInvitation,
  AcceptInvitationInput,
  CreateInvitationInput,
  InvitationListInput,
  InvitationRepository,
  TenantContext,
} from '@zetyar/data-contracts'
import type { Invitation, RoleName } from '@zetyar/domain'
import { isKnownRole } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'
import { createPersonForSelf, getMyProfile } from '../people'

interface InvitationRow {
  readonly id: string
  readonly organization_id: string
  readonly email: string
  readonly role: string
  readonly invited_by_membership_id: string | null
  readonly token: string
  readonly status: string
  readonly expires_at: string
  readonly accepted_at: string | null
  readonly created_at: string
}

function mapInvitation(row: InvitationRow): Invitation {
  return {
    id: row.id,
    organizationId: row.organization_id,
    email: row.email,
    role: isKnownRole(row.role) ? row.role : 'member',
    invitedBy: row.invited_by_membership_id,
    token: row.token,
    status: row.status as Invitation['status'],
    expiresAt: row.expires_at,
    acceptedAt: row.accepted_at,
    createdAt: row.created_at,
  }
}

export function createSupabaseInvitationRepository(client: SupabaseClient): InvitationRepository {
  return {
    async listPending(context: TenantContext, { limit }: InvitationListInput) {
      const { data, error } = await client
        .from('invitations')
        .select('id, organization_id, email, role, invited_by_membership_id, token, status, expires_at, accepted_at, created_at')
        .eq('organization_id', context.organizationId)
        .eq('status', 'pending')
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as InvitationRow[]).map(mapInvitation)
    },

    async create(context: TenantContext, input: CreateInvitationInput) {
      const token = crypto.randomUUID().replace(/-/g, '')
      const expiresAt = new Date(Date.now() + (input.expiresInDays ?? 7) * 24 * 60 * 60 * 1000).toISOString()

      const { data, error } = await client
        .rpc('create_invitation', {
          p_organization_id: context.organizationId,
          p_email: input.email,
          p_role: input.role,
          p_token: token,
          p_expires_at: expiresAt,
        })
        .single()

      if (error) throw error
      return mapInvitation(data as unknown as InvitationRow)
    },

    async revoke(context: TenantContext, invitationId) {
      const { error } = await client.rpc('revoke_invitation', {
        p_id: invitationId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },

    async getByToken(token: string) {
      const { data, error } = await client
        .rpc('get_invitation_by_token', { p_token: token })
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      const row = data as {
        id: string; email: string; role: string; status: string; expires_at: string
        organization_id: string; organization_name: string
      }

      return {
        id: row.id,
        organizationId: row.organization_id,
        email: row.email,
        role: isKnownRole(row.role) ? row.role : 'member',
        invitedBy: '',
        token,
        status: row.status as Invitation['status'],
        expiresAt: row.expires_at,
        acceptedAt: null,
        createdAt: row.expires_at,
        organization: { name: row.organization_name } as Invitation['organization'],
      }
    },

    async accept(input: AcceptInvitationInput): Promise<AcceptedInvitation> {
      const invitationLookup = await client
        .rpc('get_invitation_by_token', { p_token: input.token })
        .maybeSingle()

      if (invitationLookup.error) throw invitationLookup.error
      const invitation = invitationLookup.data as {
        id: string; role: string; status: string; expires_at: string; organization_id: string
      } | null

      if (!invitation || invitation.status !== 'pending') {
        throw new Error('Invitation is not valid or already used')
      }
      if (new Date(invitation.expires_at).getTime() < Date.now()) {
        throw new Error('Invitation has expired')
      }

      const { data: authData, error: authError } = await client.auth.getUser()
      if (authError || !authData.user) throw authError ?? new Error('No authenticated user')

      const profile = await getMyProfile(client, authData.user)
      let personId = profile?.id

      if (!personId) {
        personId = await createPersonForSelf(client, authData.user, input.displayName)
      }

      const role: RoleName = isKnownRole(invitation.role) ? invitation.role : 'member'

      const { data: membershipId, error: acceptError } = await client.rpc('accept_invitation', {
        p_invitation_id: invitation.id,
        p_organization_id: invitation.organization_id,
        p_person_id: personId,
        p_role: role,
        p_display_name: input.displayName,
        p_institutional_email: null,
      })

      if (acceptError) throw acceptError

      return { organizationId: invitation.organization_id, membershipId: membershipId as string }
    },
  }
}
